import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import { useEffect } from "react";
import { Link } from "react-router-dom";

import copy from "../../public/locales/en/analytics.json";
import { useAppDispatch, useAppSelector } from "../store";
import { fetchAdminStats } from "../store/slices/adminStatsSlice";

import type { ReactElement } from "react";

function MetricCard({
  title,
  value,
}: {
  title: string;
  value: number | null;
}): ReactElement {
  return (
    <Card>
      <CardContent>
        <Typography color="text.secondary" gutterBottom>
          {title}
        </Typography>
        <Typography variant="h4">
          {value === null ? copy.dashboard.noData : value.toLocaleString("en")}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage(): ReactElement {
  const dispatch = useAppDispatch();
  const { stats, loading, error } = useAppSelector((state) => state.adminStats);
  useEffect((): void => {
    void dispatch(fetchAdminStats());
  }, [dispatch]);
  return (
    <Box>
      <Box
        sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 3 }}
      >
        <Typography variant="h4">{copy.dashboard.title}</Typography>
        <Button
          disabled={loading}
          onClick={(): void => {
            void dispatch(fetchAdminStats());
          }}
        >
          {copy.refresh}
        </Button>
      </Box>
      {loading && <CircularProgress size={28} />}
      {error && <Alert severity="error">{copy.error}</Alert>}
      {stats && (
        <>
          <Typography variant="h6" gutterBottom>
            {copy.dashboard.now}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <MetricCard
                title={copy.dashboard.users}
                value={stats.totalUsers}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <MetricCard
                title={copy.dashboard.subscriptions}
                value={stats.activeSubscriptions}
              />
            </Grid>
          </Grid>
          <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
            {copy.dashboard.today} · {stats.date}
          </Typography>
          <Grid container spacing={2}>
            {(
              [
                [copy.views, stats.today.landingViews],
                [copy.registered, stats.today.registrations],
                [copy.activeUsers, stats.today.activeUsers],
                [copy.newSubscriptions, stats.today.newSubscriptions],
              ] as const
            ).map(([title, value]) => (
              <Grid key={title} size={{ xs: 12, sm: 6, lg: 3 }}>
                <MetricCard title={title} value={value} />
              </Grid>
            ))}
          </Grid>
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            {copy.dashboard.note}
          </Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
            {copy.trafficNote}
          </Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
            {copy.subscriptionsNote}
          </Typography>
        </>
      )}
      <Button
        component={Link}
        to="/analytics"
        variant="outlined"
        sx={{ mt: 3 }}
      >
        {copy.dashboard.analytics}
      </Button>
    </Box>
  );
}
